import { shallowEqual, useSelector } from 'react-redux'
import React, { useEffect, useMemo, useState } from 'react'
import { aquaFetch } from '../../../common/utils/ActionUtils'
import ApplicationConf from '../../../conf/ApplicationConf'
import { groupBy, keyBy, keys, minBy, orderBy } from 'lodash'
import { STATUS } from '../../../common/constants/storeConstants'
import PropTypes from 'prop-types'
import { Button, Card, Dialog, DialogContent, Grid } from '@mui/material'
import { CardTitle } from '../../../common/components/card/Card'
import CommercialFeatureExplorer from '../../commercial/commercialFeature/components/CommercialFeatureExplorer'
import { BasicDialogTitle } from '../../../common/components/modal/DialogComponents'
import ReactECharts from 'echarts-for-react'
import Moment from 'moment'
import { extendMoment } from 'moment-range'
import { NewTable } from '../../../common/components/datatable/Table'
import Autocomplete from '../../../common/components/form/Autocomplete'
import useFetch from '../../../common/hooks/useFetch'

const ElemList = ({ list, mode, onClick, selected }) => (
    <>
        <Autocomplete
            items={list.map(l => ({ id: l[0], label: l[0] }))}
            value={selected}
            onChange={onClick}
            label={mode === 'user' ? 'Utilisateur' : 'Action'}
        />
        <NewTable
            headers={[
                { key: 'key', value: mode === 'user' ? 'Top Utilisateurs' : 'Top Actions' },
                { key: 'count', value: '#' },
            ]}
            rowsPerPageDefault={10}
            rows={list.map(l => ({ key: { value: l[0], leftIcon: mode === 'user' && { icon: 'person', color: l[2] } }, count: l[1] }))}
            onClickRow={row => onClick(row.key.value === selected ? undefined : row.key.value)}
        />
    </>
)

ElemList.propTypes = {
    list: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.oneOf([PropTypes.string, PropTypes.number, PropTypes.string]))),
    mode: PropTypes.string,
    onClick: PropTypes.func,
    selected: PropTypes.string,
}

const moment = extendMoment(Moment)

const enumerateBetweenDates = (start, end, format = 'days', step = 1) => {
    return Array.from(moment().range(moment(start).startOf(format).valueOf(), moment(end).endOf(format).valueOf()).by(format, { step }))
}

const StatPopup = ({
    group: defaultGroup,
    commercialFeature,
    instances = [],
    onClose = () => {},
}) => {
    // const [stats, setStats] = useState({ stats: [], actions: [] })
    const instance = instances.find(i => i.instanceType?.toLowerCase()?.trim() === 'prod') ?? instances[0]

    const [group, setGroup] = useState(defaultGroup)
    const [user, setSelectedUser] = useState(undefined)
    const [action, setSelectedAction] = useState(undefined)

    const {
        data: stats = { stats: [], actions: [] },
    } = useFetch(ApplicationConf.gdl.customerStats(), {
        method: 'POST',
        body: { commercialFeature, instanceIds: [instance.instance], group, action, user },
    })

    const colors = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc']

    const { xAxisType, min, categories, series, users } = (() => {
        const groupUser = groupBy(stats.stats, 'user')
        const orderedUsers = orderBy(keys(groupUser).filter(u => u !== 'undefined').map(u => [u, groupUser[u].length]), tab => tab[1], 'desc')
        switch (group) {
            case '30J':
                return {
                    xAxisType: 'category',
                    categories: enumerateBetweenDates(moment().subtract(1, 'month').startOf('day').valueOf(), moment().endOf('day').valueOf(), 'hours', 2).map(d => d.format('DD/MM/YYYY HH:mm')),
                    series: Object.keys(groupUser).map(key => ({
                        type: 'bar',
                        name: key,
                        data: groupUser[key].map(s => [moment(s.date).format('DD/MM/YYYY HH:mm'), s.count]),
                        smooth: true,
                        stack: 'all',
                        color: colors[orderedUsers.findIndex(u => u[0] === key) % 10],
                    })),
                    users: orderedUsers.map(([u, count], i) => [u, count, colors[i % 10]]),
                }
            case '3MO':
                return {
                    xAxisType: 'category',
                    categories: enumerateBetweenDates(moment().subtract(3, 'months').startOf('day').valueOf(), moment().endOf('day').valueOf(), 'days').map(d => d.format('DD/MM/YYYY')),
                    series: keys(groupUser).map(key => ({
                        type: 'bar',
                        name: key,
                        data: groupUser[key].map(s => [moment(s.date).format('DD/MM/YYYY'), s.count]),
                        smooth: true,
                        stack: 'all',
                        color: colors[orderedUsers.findIndex(u => u[0] === key) % 10],
                    })),
                    users: orderedUsers.map(([u, count], i) => [u, count, colors[i % 10]]),
                }
            case '1Y':
                return {
                    xAxisType: 'category',
                    categories: enumerateBetweenDates(moment().subtract(1, 'year').valueOf(), moment().valueOf(), 'days').map(d => d.format('DD/MM/YYYY')),
                    series: keys(groupUser).map(key => ({
                        type: 'bar',
                        name: key,
                        data: groupUser[key].map(s => [moment(s.date).format('DD/MM/YYYY'), s.count]),
                        smooth: true,
                        stack: 'all',
                        color: colors[orderedUsers.findIndex(u => u[0] === key) % 10],
                    })),
                    users: orderedUsers.map(([u, count], i) => [u, count, colors[i % 10]]),
                }
            case 'HISTO':
                return {
                    xAxisType: 'category',
                    categories: enumerateBetweenDates(minBy(stats.stats, 'date')?.date, moment().valueOf(), 'month').map(d => d.format('MM/YYYY')),
                    series: keys(groupUser).map(key => ({
                        type: 'bar',
                        name: key,
                        // vue mensuelle : la clé doit matcher l'axe (mois), pas l'année
                        data: groupUser[key].map(s => [moment(s.date).format('MM/YYYY'), s.count]),
                        smooth: true,
                        stack: 'all',
                        color: colors[orderedUsers.findIndex(u => u[0] === key) % 10],
                    })),
                    users: orderedUsers.map(([u, count], i) => [u, count, colors[i % 10]]),
                }
            default:
                return {}
        }
    })()

    const actions = orderBy(stats.actions.map(a => [a.action, a.count]), a => a[1], 'desc')

    const options = {
        series,
        xAxis: [{
            type: xAxisType,
            data: categories,
            axisLabel: {
                formatter: undefined,
            },
            splitLine: { show: true },
            axisLine: { show: true },
            axisTick: { show: true },
            min,
            max: undefined,
        }],
        yAxis: [{
            type: 'value',
            // axisLabel: {
            //     formatter: shortenHumanize,
            // },
            axisLine: { show: true },
            axisTick: { show: true },
            splitLine: { show: true },
            min: 0,
            // max: ({ max }) => max * 1.05,
        }],
        dataZoom: [
            {},
            {
                type: 'inside',
            },
        ],
        grid: { top: 20, right: 20, bottom: 20, left: 60, height: 500 },
        tooltip: {
            trigger: 'axis',
        },
    }
    return (
        <Dialog
            open={true}
            maxWidth={'xl'}
        >
            <BasicDialogTitle onClose={onClose}>
                Statistiques
            </BasicDialogTitle>
            <DialogContent style={{ overflowX: 'hidden', paddingRight: 25 }}>
                <Grid container>
                    <Grid size={8} container>
                        <Grid size={12} container spacing={1}>
                            <Grid>
                                <Button variant={group === 'HISTO' ? 'contained' : 'outlined'} onClick={() => setGroup('HISTO')}>Histo</Button>
                            </Grid>
                            <Grid>
                                <Button variant={group === '1Y' ? 'contained' : 'outlined'} onClick={() => setGroup('1Y')}>1 an</Button>
                            </Grid>
                            <Grid>
                                <Button variant={group === '3MO' ? 'contained' : 'outlined'} onClick={() => setGroup('3MO')}>3 mois</Button>
                            </Grid>
                            <Grid>
                                <Button variant={group === '30J' ? 'contained' : 'outlined'} onClick={() => setGroup('30J')}>30 jours</Button>
                            </Grid>
                            <Grid size='grow'/>
                        </Grid>
                        <Grid size={12}>
                            <ReactECharts
                                option={options}
                                notMerge={true}
                                lazyUpdate={true}
                                style={{ height: 600 }}
                            />
                        </Grid>
                    </Grid>
                    <Grid size={4}>
                        { users?.length !== 0 && <ElemList list={users} mode='user' onClick={setSelectedUser} selected={user}/> }
                        <div style={{ padding: 5 }} />
                        { actions?.length !== 0 && <ElemList list={actions} mode='action' onClick={setSelectedAction} selected={action}/> }
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    )
}

StatPopup.propTypes = {
    group: PropTypes.string,
    commercialFeature: PropTypes.number, // id
    instances: PropTypes.arrayOf(PropTypes.shape({})),
    onClose: PropTypes.func,
}

const ModuleSynthesis = ({
    customer = {},
}) => {
    const { commercialFeaturesIds, licensesSynthesis, commercialFeaturesStatus } = useSelector(store => ({
        commercialFeaturesIds: store.gdl.commercialFeatures.byId,
        commercialFeaturesStatus: store.gdl.commercialFeatures.status,
        licensesSynthesis: store.gdl.licenses.licensesSynthesis,
    }), shallowEqual)
    const [statsSynthesis, setStatsSynthesis] = useState([])
    const [popupStatIsOpen, setOpen] = useState(false)
    const [selectedGroup, setSelectedGroup] = useState(undefined)
    const [selectedFeature, setSelectedFeature] = useState(undefined)
    const [displayAll, setDisplayAll] = useState(false)

    useEffect(() => {
        if (customer?.instances?.length) {
            aquaFetch(ApplicationConf.gdl.customerStats(), {
                method: 'POST',
                body: { instanceIds: customer.instances.map(i => i.instance), group: 'SYNTHESIS', action: 'ALL' },
            }).then(stats => setStatsSynthesis(keyBy(stats, 'idCommercialFeature')))
        }
    }, [customer])

    const formattedGroupedCommercialFeatures = useMemo(() => {
        if (!licensesSynthesis.length || commercialFeaturesStatus !== STATUS.SUCCEEDED) {
            return []
        }
        const otherModules = !displayAll ? [] : Object.keys(commercialFeaturesIds)
            .filter(id => !licensesSynthesis.some(l => l.commercialFeature === parseInt(id)))
            .map(id => ({ commercialFeature: parseInt(id), color: '#cfcfcf' }))
        const listCommercialFeatures = orderBy([...licensesSynthesis, ...otherModules].map(featureLicense => ({ ...commercialFeaturesIds[featureLicense.commercialFeature], color: featureLicense.color })), 'id')
        const orderedCommercialFeatures = orderBy(listCommercialFeatures, ['typeFeature', 'code'], ['desc', 'asc'])
        const groupedCommercialFeatures = groupBy(orderedCommercialFeatures, s => s?.parent ?? 'headTree')

        const formatCommercialFeatures = (filteredCommercialFeatures = []) => {
            return filteredCommercialFeatures.map(({ id, name, description, price, color }) => {
                const subFeatures = formatCommercialFeatures(groupedCommercialFeatures[id])
                return {
                    id,
                    label: name,
                    price,
                    description,
                    subFeatures,
                    stats: statsSynthesis[id],
                    onClickStat: (group, idFeatureCommerce) => {
                        setSelectedGroup(group)
                        setSelectedFeature(idFeatureCommerce)
                        setOpen(true)
                    },
                    color,
                    textColor: color && statsSynthesis[id]?.count_3mo !== 0 ? 'red' : undefined,
                }
            })
        }

        return formatCommercialFeatures(groupedCommercialFeatures.headTree)
    }, [licensesSynthesis, commercialFeaturesStatus, statsSynthesis, displayAll])

    const titleActions = [{
        icon: displayAll ? 'visibility' : 'visibility_off',
        color: 'black',
        tooltip: 'Afficher tous les modules',
        onClick: () => setDisplayAll(!displayAll),
    }]

    return (
        <>
            <Grid container spacing={1} alignItems='flex-start'>
                <Grid size={12}><Card><CardTitle title={ displayAll ? 'Synthèse de tous les modules' : 'Synthèse des Modules accessibles' } actions={titleActions}/></Card></Grid>
            </Grid>
            <Grid container spacing={1} alignItems='flex-start' sx={{ padding: '5px' }}>
                {formattedGroupedCommercialFeatures.map(commercialFeature => (
                    <Grid key={commercialFeature.code} size={12}>
                        <CommercialFeatureExplorer
                            commercialFeature={commercialFeature}
                        />
                    </Grid>
                ))}
            </Grid>
            {
                popupStatIsOpen && (
                    <StatPopup
                        instances={customer?.instances}
                        group={selectedGroup}
                        commercialFeature={selectedFeature}
                        onClose={() => setOpen(false)}
                    />
                )
            }
        </>
    )
}

ModuleSynthesis.propTypes = {
    customer: PropTypes.shape({}),
}

export default ModuleSynthesis